import { forwardRef, useCallback } from "react";
import FormattedNumberInput, {
  NumberFormatPropsBase,
} from "react-number-format";
import {
  Input,
  InputProps,
  omitThemingProps,
  ThemingProps,
  UseFormControlProps,
  useFormControlProps,
  useNumberInput,
  UseNumberInputProps,
} from "@chakra-ui/react";

type HTMLInputProps = React.InputHTMLAttributes<HTMLInputElement>;

type NumberFormatPropsWithoutInput = Omit<
  NumberFormatPropsBase<HTMLInputProps>,
  keyof Omit<
    HTMLInputProps,
    "value" | "defaultValue" | "onKeyDown" | "onMouseUp" | "onFocus" | "onBlur"
  >
>;

function getOnChangeValue(
  value: string,
  asInteger = false,
): Decimal | number | null {
  if (!value) return null;
  if (asInteger) return parseInt(value, 10);
  return value;
}

export type Props = Omit<InputProps, "onChange" | "value"> &
  Omit<UseNumberInputProps, "onChange" | "value"> &
  NumberFormatPropsWithoutInput & {
    onChange?: (value: Decimal | number | null) => void;
    value?: UseNumberInputProps["value"] | null;
  };

// eslint-disable-next-line react/display-name
const NumberInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    value,
    onChange,
    onValueChange,
    min,
    autoComplete = "off",
    ...rest
  } = props;

  const propsWithDefaults = {
    ...rest,
    min: rest.allowNegative === false ? 0 : min,
  };

  const isInteger = rest.decimalScale === 0;

  const ownProps = omitThemingProps<ThemingProps<"Input">>(propsWithDefaults);
  const controlProps = useFormControlProps(
    ownProps as UseFormControlProps<HTMLInputElement>,
  ) as ReturnType<typeof useFormControlProps> & typeof ownProps;

  const { getInputProps } = useNumberInput({
    ...controlProps,
    onChange: (strValue) => onChange?.(getOnChangeValue(strValue, isInteger)),
    value: value ?? "",
  });

  const {
    onChange: onInputChange,
    ref: inputRef,
    ...inputProps
  } = getInputProps(propsWithDefaults, ref);

  const handleValueChange = useCallback<
    Required<NumberFormatPropsWithoutInput>["onValueChange"]
  >(
    (values, source) => {
      onValueChange?.(values, source);
      if (source.source !== "prop" && source.event) {
        onInputChange?.(source.event);
      }
    },
    [onValueChange, onInputChange],
  );

  return (
    <Input
      getInputRef={inputRef}
      as={FormattedNumberInput}
      onValueChange={handleValueChange}
      isNumericString={!isInteger}
      // Disable autocomplete by default for these since sometimes password
      // managers like to think they can fill formatted inputs
      autoComplete={autoComplete}
      data-1p-ignore={autoComplete === "off" || undefined}
      data-lpignore={autoComplete === "off" || undefined}
      {...inputProps}
      onFocus={(e) => {
        inputProps.onFocus?.({
          ...e,
          currentTarget: e.currentTarget || e.target,
        });
      }}
    />
  );
});

export default NumberInput;
